import type { Marcado } from "@noesis/shared";

/**
 * Fila de escrita offline — o que permite responder no ônibus sem sinal.
 *
 * **Reusa a idempotência que já existe desde a F1.** O `idCliente` nasce no
 * navegador e `resposta` tem índice único em `(usuarioId, idCliente)`: reenviar
 * já era inofensivo, com teste. A fila não precisa de protocolo novo — só
 * precisa guardar o que não saiu e mandar de novo depois.
 *
 * **IndexedDB, não localStorage.** A fila precisa sobreviver a fechar o app e a
 * ficar dias sem sinal, e `localStorage` é síncrono e limitado a alguns
 * megabytes compartilhados com o resto. Aqui também moram os itens baixados
 * para o modo ônibus, que incluem enunciado e explicação.
 *
 * **O iOS não tem Background Sync.** A sincronização acontece quando o app é
 * aberto com sinal, ou no evento `online` com o app na frente — nunca sozinha
 * no bolso. O contador de pendentes existe para isso nunca ser surpresa.
 */

const BANCO = "noesis";
const VERSAO = 1;
const LOJA_FILA = "fila";
const LOJA_PACOTE = "pacote";

export interface RespostaPendente {
  idCliente: string;
  itemId: string;
  marcado: Marcado;
  tempoMs: number;
  /** Quando foi respondida, para a ordem de envio ser a ordem real. */
  criadaEm: string;
  /** Quantas vezes já se tentou enviar. Só informativo. */
  tentativas: number;
}

/** Um item com gabarito, baixado para o aparelho pelo modo ônibus. */
export interface ItemComGabarito {
  itemId: string;
  gabarito: unknown;
  explicacao: string | null;
  /** O conteúdo completo, incluindo as respostas das lacunas do cloze. */
  conteudo: unknown;
  tipo: string;
}

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolver, rejeitar) => {
    const req = indexedDB.open(BANCO, VERSAO);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(LOJA_FILA)) db.createObjectStore(LOJA_FILA, { keyPath: "idCliente" });
      if (!db.objectStoreNames.contains(LOJA_PACOTE)) db.createObjectStore(LOJA_PACOTE, { keyPath: "itemId" });
    };
    req.onsuccess = () => resolver(req.result);
    req.onerror = () => rejeitar(req.error ?? new Error("IndexedDB indisponível"));
  });
}

function comLoja<T>(loja: string, modo: IDBTransactionMode, corpo: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return abrir().then(
    (db) =>
      new Promise<T>((resolver, rejeitar) => {
        const tx = db.transaction(loja, modo);
        const req = corpo(tx.objectStore(loja));
        req.onsuccess = () => resolver(req.result);
        req.onerror = () => rejeitar(req.error ?? new Error("Falha no IndexedDB"));
        tx.oncomplete = () => db.close();
      }),
  );
}

export const enfileirar = (r: RespostaPendente) => comLoja(LOJA_FILA, "readwrite", (s) => s.put(r));
export const lerFila = () => comLoja<RespostaPendente[]>(LOJA_FILA, "readonly", (s) => s.getAll());
export const tirarDaFila = (idCliente: string) => comLoja(LOJA_FILA, "readwrite", (s) => s.delete(idCliente));
export const contarPendentes = () => comLoja<number>(LOJA_FILA, "readonly", (s) => s.count());

export const guardarPacote = async (itens: ItemComGabarito[]) => {
  for (const i of itens) await comLoja(LOJA_PACOTE, "readwrite", (s) => s.put(i));
};
export const lerDoPacote = (itemId: string) =>
  comLoja<ItemComGabarito | undefined>(LOJA_PACOTE, "readonly", (s) => s.get(itemId));
export const limparPacote = () => comLoja(LOJA_PACOTE, "readwrite", (s) => s.clear());

/** Apaga tudo. Chamado ao sair: os gabaritos baixados não sobrevivem à sessão. */
export async function limparTudo(): Promise<void> {
  await comLoja(LOJA_FILA, "readwrite", (s) => s.clear());
  await limparPacote();
}

/**
 * O interruptor do modo ônibus.
 *
 * Mora no navegador, e não no banco, porque é **preferência do aparelho**: faz
 * sentido ligado no celular e desligado no desktop, do mesmo jeito que o tema.
 * Desligado — que é o padrão — nada muda em relação à F1: o gabarito não desce.
 */
const CHAVE_MODO = "noesis.modo-onibus";

export function modoOnibusLigado(): boolean {
  try {
    return localStorage.getItem(CHAVE_MODO) === "1";
  } catch {
    return false;
  }
}

export function definirModoOnibus(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE_MODO, ligado ? "1" : "0");
  } catch {
    /* navegador sem armazenamento: o modo simplesmente não persiste */
  }
  if (!ligado) void limparPacote();
}
